import { getSharedState, type SharedVuexState, subscribeKey } from '@frontend-platform/host-state';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Reactive read of one `@frontend-platform/host-state` key, so a host-side Vuex mutation
 * re-renders the consumer instead of leaving a value frozen at first read.
 *
 * Lives here rather than in `@frontend-platform/host-state` on purpose: that module is a
 * Module Federation singleton declared `eager: true` on the host (see
 * `@frontend-platform/federation-runtime/shared`), and the host is Vue — importing React into
 * it would pull React into aistudio's own bundle. The hook is the React
 * binding for the channel, so it belongs on the React side of it.
 *
 * Scoped to a single key rather than subscribing to the whole store: a
 * consumer that only reads `bots` must not re-render every time `user` or
 * `environments` is republished.
 *
 * `select` must return a referentially stable value for an unchanged store —
 * a primitive, or a reference read straight off state. Deriving a fresh
 * object/array inside it makes every render report a new snapshot and loops
 * forever; derive with `useMemo` on the returned value instead. Define it at
 * module scope so the subscription isn't torn down and re-established on
 * every render.
 */
export function useSharedKey<K extends keyof SharedVuexState, T>(
  key: K,
  select: (value: SharedVuexState[K] | undefined) => T
): T {
  const subscribe = useCallback((onChange: () => void) => subscribeKey(key, onChange), [key]);
  const getSnapshot = useCallback(() => select(getSharedState()?.[key]), [key, select]);
  return useSyncExternalStore(subscribe, getSnapshot);
}
